import { Box, Chip, IconButton, Typography } from "@mui/material"
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined, VisibilityOutlined } from "@mui-symbols-material/w300"
import { Link } from "@tanstack/react-router"
import type { ColumnDef } from "@tanstack/react-table"

import { CustomerProductAssociationRowActions } from "../components/CustomerProductAssociationRowActions"
import { PRIORITY_STATUS_CLASS_MAP, PRIORITY_STATUS_LABELS } from "../constants"

import type { CompanyProductAssociationListItem } from "./useGetCompanyProductAssociations"

import { ShipToLocationCell } from "@/components/table"
import { ProductStatusCell } from "@/components/table/ProductStatusCell"
import type { Product } from "@/graphql/codegen/graphql"
import { PRODUCT_STOCK_TYPE_CHIP_COLOR, PRODUCT_STOCK_TYPE_LABEL_MAP } from "@/screens/Products/constants"

interface UseCustomerProductAssociationColumnsProps {
  onEdit: (item: CompanyProductAssociationListItem) => void
  onRemove: (item: CompanyProductAssociationListItem) => Promise<boolean>
  canEdit: boolean
}

export const useCustomerProductAssociationColumns = ({
  onEdit,
  onRemove,
  canEdit,
}: UseCustomerProductAssociationColumnsProps): ColumnDef<CompanyProductAssociationListItem>[] => {
  return [
    {
      accessorKey: "productId",
      header: "Product ID",
      enableSorting: true,
      cell: ({ row }) => {
        const product = row.original.product
        if (!product) return null
        return <ProductStatusCell product={product as unknown as Product} />
      },
      maxSize: 140,
    },
    {
      accessorKey: "product.companyProductDescription",
      header: "Akrochem Desc.",
      enableSorting: true,
      cell: ({ row }) => (
        <Typography variant='body2'>{row.original.product?.companyProductDescription ?? "-"}</Typography>
      ),
    },
    {
      accessorKey: "customerDescription",
      header: "Customer Desc.",
      enableSorting: true,
      cell: ({ row }) => <Typography variant='body2'>{row.original.customerDescription ?? "-"}</Typography>,
    },
    {
      accessorKey: "locationCustomerCode",
      header: "Customer Code",
      enableSorting: true,
      cell: ({ row }) => {
        const codes = row.original.locationCustomerCode ?? []
        return (
          <Box className='flex flex-wrap gap-1'>
            {codes.map((code) => (
              <Chip key={code} size='small' label={code} className='rounded-[4px] bg-gray-300 text-xs' />
            ))}
            {!codes.length && <Typography variant='body2'>-</Typography>}
          </Box>
        )
      },
    },
    {
      accessorKey: "product.company.name",
      header: "Supplier",
      enableSorting: true,
      cell: ({ row }) => <Typography variant='body2'>{row.original.product?.company?.name ?? "-"}</Typography>,
    },
    {
      id: "shipFrom",
      accessorFn: (row) => row.product?.locationsAssociations?.[0]?.shipping?.name ?? "",
      header: "Ship From",
      enableSorting: true,
      cell: ({ row }) => {
        const name = row.original.product?.locationsAssociations?.[0]?.shipping?.name
        return <Typography variant='body2'>{name ?? "-"}</Typography>
      },
    },
    {
      id: "shipTo",
      accessorFn: (row) => row.shipToLocation?.name ?? "",
      header: "Ship To",
      enableSorting: true,
      cell: ({ row }) => <ShipToLocationCell location={row.original.shipToLocation} />,
    },
    {
      id: "stock",
      accessorFn: (row) => row.product?.stockType ?? "",
      header: "Stock",
      enableSorting: true,
      cell: ({ row }) => {
        const stockType = row.original.product?.stockType
        if (!stockType) return <Typography variant='body2'>-</Typography>
        return (
          <Chip
            label={PRODUCT_STOCK_TYPE_LABEL_MAP[stockType]}
            color={PRODUCT_STOCK_TYPE_CHIP_COLOR[stockType]}
            size='small'
          />
        )
      },
      size: 100,
    },
    {
      id: "priority",
      accessorFn: (row) => row.priorityStatus ?? "",
      header: "Priority",
      enableSorting: true,
      cell: ({ row }) => {
        const status = row.original.priorityStatus
        if (!status) return <Typography variant='body2'>-</Typography>
        return (
          <Chip
            size='small'
            label={PRIORITY_STATUS_LABELS[status]}
            className={`rounded-[2px] text-xs font-normal leading-[18px] tracking-[0.16px] text-[rgba(0,0,0,0.87)] ${PRIORITY_STATUS_CLASS_MAP[status]}`}
          />
        )
      },
      size: 100,
    },
    {
      id: "preferences",
      header: "",
      enableSorting: false,
      cell: ({ row }) => (
        <Link
          to='/app/product/$productId/preference'
          params={{ productId: row.original.productId.toString() }}
          search={{ companyId: row.original.companyId }}
          className='flex items-center gap-1 text-primary no-underline'
        >
          <VisibilityOutlined sx={{ fontSize: 18 }} />
          <Typography variant='body2' className='text-primary'>
            Product Preferences
          </Typography>
        </Link>
      ),
      size: 120,
    },
    ...(canEdit
      ? ([
          {
            id: "actions",
            header: "",
            enableSorting: false,
            cell: ({ row }) => <CustomerProductAssociationRowActions row={row} onEdit={onEdit} onRemove={onRemove} />,
            size: 48,
          },
        ] as ColumnDef<CompanyProductAssociationListItem>[])
      : []),
    {
      id: "expander",
      header: "",
      enableSorting: false,
      size: 48,
      cell: ({ row }) => {
        if (!row.original.companyProductAssociationLocationId) return null
        return (
          <IconButton size='small' onClick={row.getToggleExpandedHandler()}>
            {row.getIsExpanded() ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
          </IconButton>
        )
      },
    },
  ]
}
